/**
 * Permission Checking Utilities
 * Provides helper functions for permission checks throughout the app
 */

/**
 * Check if user can view a tab
 * @param {Object} store - Vuex store
 * @param {String} tab - Tab name
 * @returns {Boolean}
 */
export function canViewTab(store, tab) {
  return store.getters['permissions/canView'](tab)
}

/**
 * Check if user can edit a tab
 * @param {Object} store - Vuex store
 * @param {String} tab - Tab name
 * @returns {Boolean}
 */
export function canEditTab(store, tab) {
  return store.getters['permissions/canEdit'](tab)
}

/**
 * Check if user can view a settings sub-tab
 * @param {Object} store - Vuex store
 * @param {String} subTab - Settings sub-tab name
 * @returns {Boolean}
 */
export function canViewSettingsTab(store, subTab) {
  return store.getters['permissions/canViewSettingsTab'](subTab)
}

/**
 * Check if user can edit a settings sub-tab
 * @param {Object} store - Vuex store
 * @param {String} subTab - Settings sub-tab name
 * @returns {Boolean}
 */
export function canEditSettingsTab(store, subTab) {
  return store.getters['permissions/canEditSettingsTab'](subTab)
}

/**
 * Get list of accessible tabs for the user
 * @param {Object} store - Vuex store
 * @returns {Array}
 */
export function getAccessibleTabs(store) {
  return store.getters['permissions/accessibleTabs']
}

/**
 * Get list of accessible settings sub-tabs for the user
 * @param {Object} store - Vuex store
 * @returns {Array}
 */
export function getAccessibleSettingsTabs(store) {
  return store.getters['permissions/accessibleSettingsTabs']
}

/**
 * Check if user is workspace owner
 * @param {Object} store - Vuex store
 * @returns {Boolean}
 */
export function isOwnerUser(store) {
  return store.getters['permissions/isOwnerUser']
}

/**
 * Check if permissions have been loaded
 * @param {Object} store - Vuex store
 * @returns {Boolean}
 */
export function isPermissionsLoaded(store) {
  return store.getters['permissions/isPermissionsLoaded']
}

/**
 * Map route name to tab
 * @param {String} routeName - Route name from router
 * @returns {String} - Tab identifier
 */
export function mapRouteToTab(routeName) {
  const routeTabMap = {
    dashboard: 'dashboard',
    projects: 'activeProjects',
    archive: 'archivedProjects',
    'archived-project-folder': 'archivedProjects',
    'archived-document': 'archivedProjects',
    payments: 'payments',
    invoices: 'invoices',
    'account-setting': 'settings',
    settings: 'settings',
  }

  return routeTabMap[routeName] || null
}

/**
 * Map route name to settings sub-tab
 * @param {String} routeName - Route name from router
 * @returns {String} - Settings sub-tab identifier
 */
export function mapRouteToSettingsTab(routeName) {
  const settingsTabMap = {
    'account-setting-account': 'accounts',
    'account-setting-password': 'security',
    'account-setting-security': 'security',
    'account-setting-notification': 'notifications',
    subscriptions: 'billingPlans',
    seats: 'seats',
  }

  return settingsTabMap[routeName] || null
}

/**
 * Get first accessible tab for redirect
 * @param {Object} store - Vuex store
 * @returns {String} - Tab route name
 */
export function getFirstAccessibleTab(store) {
  const accessible = getAccessibleTabs(store)
  if (accessible.length === 0) return null

  const tabToRoute = {
    dashboard: 'dashboard',
    activeProjects: 'projects',
    archivedProjects: 'archive',
    payments: 'payments',
    invoices: 'invoices',
    settings: 'account-setting',
  }

  return tabToRoute[accessible[0]]
}
